import { Button, Form, Popconfirm, Space } from "antd";
import type { TableColumnsType } from "antd";
import useFetchData from "../../../../hooks/useFetchData";
import { DormApplicationService } from "../DormApplicationsService";
import { DeleteOutlined, EditOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { DormitoryDto, DormitoryFloorGenderDto, DormitoryRoomDto } from "../../../../interfaces/DormitoryDto";
import { useCallback, useState } from "react";
import useDictionary from "../../../../hooks/useDictionary";
import useModal from "../../../../ui/Modal/useModal";


const useDormitory = () => {
    const { data: dorms, fetchData: fetchDorms, isLoading: isLoadingDorm } = useFetchData<DormitoryDto[]>(DormApplicationService.getAllDorms, true);
    const { dictionary } = useDictionary({});

    const [dormGenderFloor, setDormGenderFloor] = useState<DormitoryFloorGenderDto[]>([]);
    const [currentDormId, setCurrentDormId] = useState<number | null>(null);
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

    const [dormForm] = Form.useForm();
    const [dormRoomForm] = Form.useForm();
    const [dormFloorForm] = Form.useForm();

    const { isModalOpen: dormModalOpen, showModal: showDormModal, handleCancel: cancelDormModal } = useModal();
    const { isModalOpen: dormRoomModalOpen, showModal: showDormRoomModal, handleCancel: cancelDormRoomModal } = useModal();
    const { isModalOpen: dormFloorModalOpen, showModal: showDormFloorModal, handleCancel: cancelDormFloorModal } = useModal();

    const handleOpenDormModal = (values?: DormitoryDto) => {
        dormForm.setFieldsValue(values);
        showDormModal();
    }

    const handleOpenDormRoomModal = (dormId: number, values?: DormitoryRoomDto) => {
        dormRoomForm.setFieldsValue(values);
        showDormRoomModal();
        setCurrentDormId(dormId);
    }

    const handleOpenDormFloorModal = (dormId: number, floor: number, values?: DormitoryFloorGenderDto) => {
        dormFloorForm.setFieldsValue({ ...values, floor: floor });
        showDormFloorModal();
        setCurrentDormId(dormId);
    }

    const handleCancelDormModal = () => {
        dormForm.resetFields();
        cancelDormModal();
    }

    const handleCancelDormRoomModal = () => {
        dormRoomForm.resetFields();
        cancelDormRoomModal();
    }

    const handleCancelDormFloorModal = () => {
        dormFloorForm.resetFields();
        cancelDormFloorModal();
    }

    const handleCreateUpdateDorm = () => {
        DormApplicationService.createOrUpdateDorm(dormForm.getFieldsValue())
            .then(() => {
                handleCancelDormModal();
                dormForm.resetFields();
            })
            .finally(() => fetchDorms());
    }

    const handleCreateUpdateDormRoom = (dormId: number) => {
        DormApplicationService.createOrUpdateDormRoom(dormId, dormRoomForm.getFieldsValue())
            .then(() => {
                handleCancelDormRoomModal();
                dormRoomForm.resetFields();
            })
            .finally(() => fetchDorms());
    }

    const handleCreateUpdateFloorGender = (dormId: number) => {
        DormApplicationService.createOrUpdateFloorGender(dormId, dormFloorForm.getFieldsValue())
            .then(() => {
                handleCancelDormFloorModal();
                dormFloorForm.resetFields();
            })
            .finally(() => fetchDormGenderFloor(dormId));
    }

    const handleDeleteDorm = (dormId: number) => {
        DormApplicationService.deleteDorm(dormId)
            .then(() => fetchDorms());
    }

    const handleDeleteRoom = (roomId: number) => {
        DormApplicationService.deleteRoom(roomId)
            .then(() => fetchDorms());
    }

    const handleDeleteFloor = (dormId: number, floor: number) => {
        DormApplicationService.deleteFloor(dormId, floor)
            .then(() => {
                fetchDorms();
                fetchDormGenderFloor(dormId)
            });
    }

    const handleExpand = (expanded: boolean, record: any) => {
        setExpandedRowKeys(expanded ? [record.id] : []);
    };

    const fetchDormGenderFloor = useCallback((dormId: number) => {
        setCurrentDormId(dormId);
        DormApplicationService.getFloorGender(dormId)
            .then((response) => {
                setDormGenderFloor(response.data);
            });
    }, []);

    const findFloorGenderByFloor = (floor: number) => dormGenderFloor.find((v) => v.floor === floor);

    const handleRowColor = (floor: number) => {
        const floorGender = findFloorGenderByFloor(floor)?.gender?.id;
        const genderValue = dictionary?.genderListDorm.find((v) => v.id === floorGender)?.value;

        if (genderValue === 'Женский') {
            return 'womenRow';
        } else if (genderValue === 'Мужской') {
            return 'menRow';
        }
        return '';

    };

    const dormColumns: TableColumnsType<DormitoryDto> = [
        {
            title: '№',
            dataIndex: 'key',
            key: 'key',
            render: (text: string, record, index) => index + 1,
            fixed: 'left'
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => record.nameEn + " / " + record.nameRu + " / " + record.name,
            fixed: 'left'
        },
        {
            title: 'Address',
            dataIndex: 'address',
            key: 'address',
        },
        {
            title: "Pay Year",
            dataIndex: "payYear",
            key: "payYear",
        },
        {
            title: "Beds",
            dataIndex: "beds",
            key: "beds",
        },
        {
            title: "Floor Count",
            dataIndex: "floorCount",
            key: "floorCount",
        },
        {
            title: "Room Count",
            dataIndex: "roomCount",
            key: "roomCount",
        },
        {
            title: "Ramp Count",
            dataIndex: "rampCount",
            key: "rampCount",
        },
        {
            title: "Lift Count",
            dataIndex: "liftCount",
            key: "liftCount",
        },
        {
            title: "Elevator Count",
            dataIndex: "elevatorCount",
            key: "elevatorCount",
        },
        {
            title: "Special Toilets Count",
            dataIndex: "specialToiletsCount",
            key: "specialToiletsCount",
        },
        {
            title: "Square",
            dataIndex: "square",
            key: "square",
        },
        {
            title: "Has Special Toilets",
            dataIndex: "hasSpecialToilets",
            key: "hasSpecialToilets",
            render: (text, record) => record.hasSpecialToilets ? "Yes" : "No"
        },
        {
            title: "Has Parking",
            dataIndex: "hasParking",
            key: "hasParking",
            render: (text, record) => record.hasParking ? "Yes" : "No"
        },
        {
            dataIndex: 'action',
            key: 'action',
            fixed: 'right',
            width: 100,
            render: (text, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<EditOutlined />}
                        onClick={() => handleOpenDormModal(record)}
                    >
                        Edit
                    </Button>
                    <Button
                        icon={<PlusCircleOutlined />}
                        onClick={() => handleOpenDormRoomModal(record.id)}
                    >
                        Add Room
                    </Button>
                    <Popconfirm
                        placement="topLeft"
                        title={'Delete a dorm?'}
                        onConfirm={() => handleDeleteDorm(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button danger icon={<DeleteOutlined />}>Delete</Button>
                    </Popconfirm>
                </Space >
            ),
        }
    ];

    const dormRoomsColumns: TableColumnsType<DormitoryRoomDto> = [
        {
            title: 'Number',
            dataIndex: 'number',
            key: 'number',
        },
        {
            title: 'Flat Number',
            dataIndex: 'flat_number',
            key: 'flat_number',
        },
        {
            title: 'Beds',
            dataIndex: 'beds',
            key: 'beds',
        },
        {
            dataIndex: 'action',
            key: 'action',
            fixed: 'right',
            width: 100,
            render: (text, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<EditOutlined />}
                        onClick={() => currentDormId && handleOpenDormRoomModal(currentDormId, record)}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        placement="topLeft"
                        title={'Delete a room?'}
                        onConfirm={() => handleDeleteRoom(record.id)}
                        okText="Yes"
                        cancelText="No">
                        <Button danger icon={<DeleteOutlined />}>Delete</Button>
                    </Popconfirm>
                </Space>

            ),
        }
    ];

    return {
        dorms,
        dormGenderFloor,
        dormColumns,
        dormRoomsColumns,
        fetchDormGenderFloor,
        handleRowColor,
        findFloorGenderByFloor,
        dictionary,
        expandedRowKeys,
        handleExpand,
        currentDormId,
        modals: {
            dormForm,
            dormRoomForm,
            dormFloorForm,
            dormModalOpen,
            dormRoomModalOpen,
            dormFloorModalOpen,
            handleOpenDormModal,
            handleOpenDormRoomModal,
            handleOpenDormFloorModal,
            handleCancelDormModal,
            handleCancelDormRoomModal,
            handleCancelDormFloorModal,
            handleCreateUpdateDorm,
            handleCreateUpdateDormRoom,
            handleCreateUpdateFloorGender,
            handleDeleteDorm,
            handleDeleteRoom,
            handleDeleteFloor
        },
        isLoadingDorm
    };
};

export default useDormitory;